/** @jsxImportSource @emotion/react */

import {
  Header,
  useSession,
  formatErrorResponse,
} from '@quickbit/backoffice-core-frontend';
import {
  Typography,
  createStyles,
  Icon,
  useTheme,
  Modal,
  ListItem,
  Copy,
  useForm,
  List,
  Card,
  Input,
  UtilityButton,
  Button,
} from '@quickbit/qb-design-system';
import { cloneDeep } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUpdateEffect } from 'react-use';
import { usePlatformApi } from 'api/usePlatformApi';
import { BackofficeUsers } from 'components';
import { KeyDownAutoFocus } from 'components/KeyDownAutoFocus';
import { Platform } from 'tabs/types';
import { platformValidators } from '../../utils';
import { PlatformForm } from './components';

interface Props {
  platforms: Platform[];
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const defaultPlatformForm: Platform = {
  name: '',
  pendingMerchants: [],
  merchants: [],
  defaultPayFee: { type: 'PERCENTAGE', value: 1.25 },
  defaultOnrampFee: { type: 'PERCENTAGE', value: 1.25 },
  defaultProcessingFee: { type: 'PERCENTAGE', value: 1.25 },
  defaultGatewayFee: { type: 'FIXED', value: 0.3 },
  defaultSettlementFee: { type: 'FIXED', value: 20 },
  defaultCustomerFee: null,
  defaultCorporateWalletReceiveExternalFee: { type: 'PERCENTAGE', value: 0 },
  defaultCorporateWalletReceiveExternalFeeMinimumApplicableAmount: '100',
  defaultCorporateWalletSendInternalFee: { type: 'PERCENTAGE', value: 0 },
  defaultCorporateWalletSendExternalFee: { type: 'PERCENTAGE', value: 0 },
  defaultAllowPayOrders: true,
  defaultAllowOnrampOrders: false,
  defaultAllowProcessingOrders: false,
  defaultMinutesToCancelOrderAfter: 0,
  defaultCancelOrderOnError: false,
  defaultRetryOrderCallbacks: true,
  defaultCanAccessCreditCardInfo: false,
  defaultCanAccessPersonalInformation: false,
  defaultLayoutMode: 'ORDERS',
};

export const PlatformDetails = ({ platforms, onSuccess, onError }: Props) => {
  const navigate = useNavigate();
  const { platformId } = useParams();
  const isNewPlatform = platformId === 'new';
  const {
    addPlatform,
    updatePlatform,
    linkMerchantToPlatform,
    createPendingMerchant,
    deletePendingMerchant,
  } = usePlatformApi();

  const {
    fields: platformForm,
    setters: platformFormSetters,
    errors: platformFormErrors,
    edited: platformFormEdited,
    hasErrors: platformFormHasErrors,
  } = useForm(defaultPlatformForm, platformValidators);

  const [merchantToLinkId, setMerchantToLinkId] = useState('');

  useEffect(() => {
    if (!isNewPlatform) {
      const p = platforms.find((m) => m.uuid === platformId);
      if (!p) {
        throw Error('Could not find platform with that ID');
      }
      platformFormSetters.set(cloneDeep(p));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platforms]);

  const styles = useStyles();
  const { palette, spacing } = useTheme();
  const session = useSession();
  const [apiKey, setApiKey] = useState('');

  const {
    error: addPlatformError,
    isPending: addPlatformLoading,
    mutate: add,
  } = addPlatform((platform) => {
    setApiKey(platform.apiKey);
    onSuccess('Platform added successfully');
  });

  const { error: updatePlatformError, mutate: update } = updatePlatform(() =>
    onSuccess('Platform updated successfully')
  );

  const [pendingMerchantIdToDelete, setPendingMerchantIdToDelete] = useState<
    string | undefined
  >();

  const {
    error: linkMerchantError,
    isPending: linkMerchantLoading,
    mutate: linkMerchant,
  } = linkMerchantToPlatform(() => {
    setMerchantToLinkId('');
    onSuccess('Merchant linked to platform successfully');
  });

  const {
    error: addPendingMerchantError,
    isPending: addPendingMerchantLoading,
    mutate: addPendingMerchant,
  } = createPendingMerchant(() =>
    onSuccess('Pending merchant added successfully')
  );

  const {
    error: deletePendingMerchantError,
    isPending: deletePendingMerchantLoading,
    mutate: doDeletePendingMerchantMutate,
  } = deletePendingMerchant(() =>
    onSuccess('Pending merchant deleteed successfully')
  );

  useUpdateEffect(() => {
    if (addPlatformError) onError(formatErrorResponse(addPlatformError));
    if (linkMerchantError) onError(formatErrorResponse(linkMerchantError));
    if (deletePendingMerchantError)
      onError(formatErrorResponse(deletePendingMerchantError));
    if (updatePlatformError) {
      onError(formatErrorResponse(updatePlatformError));
    }
    if (addPendingMerchantError) {
      onError(formatErrorResponse(addPendingMerchantError));
    }
  }, [
    addPlatformError,
    updatePlatformError,
    addPendingMerchantError,
    linkMerchantError,
  ]);

  const editable = useMemo(() => {
    if (
      session.authenticated &&
      !session.permissions.includes('edit_merchants')
    )
      return false;
    if (platformForm.deletedAt) return false;
    return true;
  }, [session, platformForm.deletedAt]);

  const disableSaving = !platformFormEdited || platformFormHasErrors;

  return (
    <>
      {pendingMerchantIdToDelete && platformId && (
        <Modal
          title="Are you sure you want to remove this pending merchant?"
          closeModal={() =>
            !deletePendingMerchantLoading &&
            setPendingMerchantIdToDelete(undefined)
          }
          primaryButtonProps={{
            onClick: () => {
              doDeletePendingMerchantMutate({
                merchantId: pendingMerchantIdToDelete,
              });
              setPendingMerchantIdToDelete(undefined);
            },
            text: 'Remove',
            loading: deletePendingMerchantLoading,
          }}
          secondaryButtonProps={{
            onClick: () => setPendingMerchantIdToDelete(undefined),
            text: 'Cancel',
          }}
        >
          <KeyDownAutoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                doDeletePendingMerchantMutate({
                  merchantId: pendingMerchantIdToDelete,
                });
                setPendingMerchantIdToDelete(undefined);
              }
            }}
          />
        </Modal>
      )}
      <Header>
        <div css={styles.headerContent}>
          <div css={styles.titleContainer}>
            <div css={styles.back} onClick={() => navigate('/platforms')}>
              <Typography variant="subtitle" color={palette.grey.x300}>
                Platforms
              </Typography>
            </div>
            <div css={styles.chevron}>
              <Icon name="chevron" size={spacing.xxs} />
            </div>
            <Typography variant="subtitle">
              {isNewPlatform ? 'New platform' : platformForm.name}
            </Typography>
          </div>

          <UtilityButton
            text="Cancel"
            variant="secondary"
            onClick={() => navigate('/platforms')}
          />

          <UtilityButton
            text="Save platform"
            variant="primary"
            loading={addPlatformLoading}
            disabled={!editable || disableSaving}
            onClick={() =>
              !isNewPlatform ? update(platformForm) : add(platformForm)
            }
          />
        </div>
      </Header>
      <div css={styles.wrappingContainer}>
        <PlatformForm
          formErrors={platformFormErrors}
          formFields={platformForm}
          onSuccess={onSuccess}
          formSetters={platformFormSetters}
          editable={editable}
        />
        {!isNewPlatform && (
          <div>
            <Card>
              <div>
                <Typography variant="subtitle">Merchants</Typography>
                <div css={{ margin: spacing.xxs }} />
                <List>
                  {platformForm.merchants
                    .sort((a, b) => (a.name < b.name ? -1 : 1))
                    .map((m) => (
                      <ListItem
                        key={m.merchantId}
                        title={m.name}
                        onClick={() => navigate(`/merchants/${m.merchantId}`)}
                        action={
                          <div
                            css={{
                              transform: 'rotate(180deg)',
                              color: palette.grey.default,
                            }}
                          >
                            <Icon name="chevron" size={spacing.xxs} />
                          </div>
                        }
                      />
                    ))}
                </List>
                <div
                  css={{
                    marginTop: spacing.xxs,
                    display: 'grid',
                    gap: spacing.xxs,
                    gridTemplateColumns: '1fr auto',
                    alignItems: 'center',
                  }}
                >
                  <Input
                    placeholder="Merchant ID"
                    value={merchantToLinkId}
                    onChange={(e) => setMerchantToLinkId(e.target.value)}
                  />
                  <Button
                    text="Link Merchant"
                    onClick={() => {
                      if (!platformId || !merchantToLinkId) return;
                      linkMerchant({
                        platformId,
                        merchantId: merchantToLinkId,
                      });
                    }}
                    disabled={linkMerchantLoading || !merchantToLinkId}
                    loading={linkMerchantLoading}
                  />
                </div>
              </div>
            </Card>
            <Card css={{ marginTop: spacing.xxs }}>
              <div
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="subtitle">Pending Merchants</Typography>
                <UtilityButton
                  text="Add pending merchant"
                  onClick={() => {
                    if (!platformId) return;
                    addPendingMerchant(platformId);
                  }}
                  disabled={addPendingMerchantLoading || !editable}
                  loading={addPendingMerchantLoading}
                />
              </div>
              <div css={{ margin: spacing.xxs }} />
              <List>
                {platformForm.pendingMerchants
                  .sort((a, b) => (a.merchantId < b.merchantId ? -1 : 1))
                  .map((m) => (
                    <ListItem
                      icon="trash"
                      onClick={() => setPendingMerchantIdToDelete(m.merchantId)}
                      key={m.merchantId}
                      title={m.merchantId}
                      subcontent={
                        <UtilityButton
                          variant="text"
                          text={'Go to Signup Form'}
                          onClick={() => {
                            window.open(
                              `${process.env.REACT_APP_QB_PAY_FRONTEND_BASE_URL}/sign-up?merchant-id=${m.merchantId}`,
                              '_blank'
                            );
                          }}
                        />
                      }
                    />
                  ))}
              </List>
            </Card>
            {platformForm.uuid && (
              <div css={{ marginTop: `-${spacing.xxs}` }}>
                <BackofficeUsers
                  merchantId={platformForm.uuid}
                  onSuccess={onSuccess}
                />
              </div>
            )}
          </div>
        )}
      </div>
      {apiKey && (
        <Modal
          title="API Key"
          subtitle="Please copy this API Key and send it to the platform."
          primaryButtonProps={{
            text: 'I have copied the API Key',
            onClick: () => {
              setApiKey('');
              void navigate(`/platforms`);
            },
          }}
        >
          <div css={styles.apiKey}>
            <ListItem
              title="API Key"
              subtitle={apiKey}
              action={<Copy textToCopy={apiKey} />}
            />
          </div>
        </Modal>
      )}
    </>
  );
};

const useStyles = createStyles(({ spacing, shape, palette }) => ({
  wrappingContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing.xs,
  },
  headerContent: {
    display: 'grid',
    gap: spacing.xxs,
    gridTemplateColumns: '1fr auto auto',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  back: {
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
      textDecorationColor: palette.grey.x300,
    },
  },
  chevron: {
    transform: 'rotate(180deg)',
    padding: `0 ${spacing.xxs}`,
    svg: {
      fill: 'none',
      stroke: palette.grey.x300,
    },
  },
  apiKey: {
    li: {
      border: `1px solid ${palette.grey.x200}`,
      borderRadius: shape.borderRadius,
      color: palette.black,
    },
  },
}));
